import { useCallback, useState } from 'react';

import classNames from 'classnames';

import BpkMap, {
  BpkIconMarker,
  withGoogleMapsScript,
} from '@skyscanner/backpack-web/bpk-component-map';
import { TEXT_STYLES } from '@skyscanner/backpack-web/bpk-component-text';
import BpkText from '@skyscanner/backpack-web/bpk-component-text/src/BpkText';

import getIcon from '../common/Icon';

import InteractiveMapCarousel from './InteractiveMapCarousel';
import getBounds from './helpers/getBounds';
import getCenter from './helpers/getCenter';

import type { MapMarkerProps, MarkerContainerProps } from './types';
import type { InteractiveMapProps } from '@skyscanner-internal/landing-pages-shared-types/src/types-shared/InteractiveMapProps';

import STYLES from './InteractiveMap.module.scss';

const BpkMapWithScript =
  typeof window === 'undefined' ? BpkMap : withGoogleMapsScript(BpkMap);

const MapMarker = ({
  handleMarkerClick,
  highlightedMarker,
  index,
  position,
  typeIcon,
}: MapMarkerProps) => {
  // Prevents showing marker in a maker
  const markerIcon = typeIcon === 'cityCenter' ? 'dot' : typeIcon;
  const IconComponent = getIcon(markerIcon, 'small');

  return (
    <BpkIconMarker
      icon={<IconComponent className={STYLES.MapMarker} />}
      position={position}
      onClick={() => handleMarkerClick(index)}
      selected={highlightedMarker === index}
    />
  );
};

const MarkerContainer = ({
  handleMarkerClick,
  highlightedMarker,
  items,
}: MarkerContainerProps) => (
  <>
    {items.map((item, i) => (
      <MapMarker
        key={item.id}
        index={i}
        handleMarkerClick={handleMarkerClick}
        highlightedMarker={highlightedMarker}
        position={{
          latitude: item.mapMarker.lat,
          longitude: item.mapMarker.lng,
        }}
        typeIcon={item.typeIcon}
      />
    ))}
  </>
);

export default function InteractiveMap({
  bodyText,
  googleMapsApiKey,
  isSideBarLayout,
  items,
  mapId,
  mapPosition = 'right',
  subtitle,
  title,
}: InteractiveMapProps) {
  const [markerItem, setMarkerItem] = useState(0);
  const [carouselItem, setCarouselItem] = useState(0);
  const [markerScroll, setMarkerScroll] = useState(false);
  const [scrollMethod, setScrollMethod] = useState<'marker' | 'carousel'>(
    'marker',
  );

  // This represents the currently selected item, and is used to determine which item to highlight in the carousel and map.
  const highlightedMarker =
    scrollMethod === 'marker' ? markerItem : carouselItem;

  const handleMarkerClick = useCallback((index: number) => {
    setMarkerScroll(true);

    setScrollMethod('marker');
    setMarkerItem(index);
  }, []);

  const hasContentHeader = !!(title || subtitle || bodyText);
  const hasTitles = !!(title || subtitle);

  return (
    <div
      className={classNames(STYLES.InteractiveMap, {
        [STYLES['InteractiveMap--reverse']]: mapPosition === 'left',
        [STYLES['InteractiveMap--sidebar-layout']]: isSideBarLayout,
        [STYLES['InteractiveMap--full-Width-layout']]: !isSideBarLayout,
      })}
    >
      <div className={STYLES.Grid}>
        {hasContentHeader && (
          <div className={STYLES.ContentHeader}>
            {hasTitles && (
              <div className={STYLES.ContentHeaderTitles}>
                {title && (
                  <BpkText textStyle={TEXT_STYLES.hero5} tagName="h2">
                    {title}
                  </BpkText>
                )}
                {subtitle && (
                  <BpkText textStyle={TEXT_STYLES.editorial3} tagName="p">
                    {subtitle}
                  </BpkText>
                )}
              </div>
            )}
            {bodyText && (
              <BpkText textStyle={TEXT_STYLES.bodyLongform}>{bodyText}</BpkText>
            )}
          </div>
        )}

        <div className={STYLES.Content}>
          <InteractiveMapCarousel
            items={items}
            onCarouselItemChange={(i) => {
              if (!markerScroll) {
                setCarouselItem(i);
                setScrollMethod('carousel');
              }
            }}
            onScrollComplete={(i) => {
              setMarkerScroll(false);

              setCarouselItem(i);
              setMarkerItem(i);
            }}
            preventComplete={markerScroll}
            selectedItem={markerItem}
          />
        </div>

        <div className={STYLES.MapContainer}>
          <BpkMapWithScript
            className={STYLES.Map}
            googleMapsApiKey={googleMapsApiKey}
            mapId={mapId}
            center={getCenter(items)}
            bounds={getBounds(items)}
            showControls
            panEnabled
          >
            <MarkerContainer
              handleMarkerClick={handleMarkerClick}
              highlightedMarker={highlightedMarker}
              items={items}
            />
          </BpkMapWithScript>
        </div>
      </div>
    </div>
  );
}
